import { apiGet, apiPost, apiPut } from './apiClient';

export interface KIDDocument {
  id: string;
  share_class_id: string;
  version: number;
  status: string;
  file_path: string;
  sri_value: number;
  performance_scenarios: any;
  cost_breakdown: any;
  kid_date: string;
  generated_by: string;
  generated_at: string;
  approved_by: string | null;
  published_at: string | null;
  data_snapshot: any;
  created_at: string;
}

export interface KIDTemplate {
  id: string;
  name: string;
  fund_type: string;
  description: string;
  is_active: boolean;
}

export interface KIDSchedule {
  id: string;
  share_class_id: string;
  frequency: string;
  is_active: boolean;
  next_generation_at: string | null;
}

// Helper to unwrap { status, data } envelope from KID API
function unwrap<T>(response: any): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return response.data as T;
  }
  return response as T;
}

export const kidService = {
  generate: async (shareClassId: string, templateId: string) => {
    const res = await apiPost<any>(`/kid/generate/${shareClassId}`, { template_id: templateId });
    return unwrap<KIDDocument>(res);
  },

  getLatest: async (shareClassId: string) => {
    const res = await apiGet<any>(`/kid/${shareClassId}/latest`);
    return unwrap<KIDDocument>(res);
  },

  getHistory: async (shareClassId: string) => {
    const res = await apiGet<any>(`/kid/${shareClassId}/history`);
    return unwrap<KIDDocument[]>(res) || [];
  },

  getById: async (kidId: string) => {
    const res = await apiGet<any>(`/kid/detail/${kidId}`);
    return unwrap<KIDDocument>(res);
  },

  download: (kidId: string) =>
    apiGet<Blob>(`/kid/detail/${kidId}/download`, { responseType: 'blob' }),

  approve: async (kidId: string, comments?: string) => {
    const res = await apiPost<any>(`/kid/detail/${kidId}/approve`, { comments });
    return unwrap<KIDDocument>(res);
  },

  publish: async (kidId: string) => {
    const res = await apiPost<any>(`/kid/detail/${kidId}/publish`);
    return unwrap<KIDDocument>(res);
  },

  getTemplates: async () => {
    const res = await apiGet<any>('/kid/templates');
    return unwrap<KIDTemplate[]>(res) || [];
  },

  updateTemplate: async (id: string, data: Partial<KIDTemplate>) => {
    const res = await apiPut<any>(`/kid/templates/${id}`, data);
    return unwrap<KIDTemplate>(res);
  },

  createSchedule: async (data: Omit<KIDSchedule, 'id' | 'next_generation_at'>) => {
    const res = await apiPost<any>('/kid/schedules', data);
    return unwrap<KIDSchedule>(res);
  },

  getSchedules: async () => {
    const res = await apiGet<any>('/kid/schedules');
    return unwrap<KIDSchedule[]>(res) || [];
  },
};
